/**
 * Pure helpers of the Song & Timing workspace (W2-08 timing; DESIGN.md §4.15, §5.2). No React, no services: the
 * panels, commands and the wizard Beat step share them, and the tests exercise them directly.
 *
 *  - Offsets: the chart offset Phira hears is META.offset (integer ms, `timing.offsetMs`) plus info.yml `offset`
 *    (`pkg.infoOffsetSec`: a sub-millisecond remainder and/or an imported per-chart adjustment). The browser plays
 *    the decoded file shifted by the preview-only `editor.previewAudioDelayMs` on top (A§8), so in the decoded
 *    waveform beat 0 sits at `heard + previewAudioDelay`.
 *  - Tempo rows: the tempo-changes table merges BPM points and meter marks by beat (a row may carry a tempo, a
 *    meter, or both); values not set on a row are inherited from the row before.
 *  - Bars: music-time spans of bars under the tempo map and the meter (alignment rows, overview bar lines).
 *  - By-ear stepping: "Too early / Too late" move beat 0 by 10, 5, 2 ms, then 1 ms (DESIGN.md §5.2.3).
 *  - Tap tempo: running average after 4 taps, reset after 2 s idle, spread of the intervals (DESIGN.md §5.2.2).
 *  - BPM field steps: 0.1, Ctrl (or Alt) 0.01, Shift 1 (DESIGN.md §5.2.4).
 */

import type { OnsetEnvelope } from '../../core/audio-dsp/types';
import { infoAdjustmentMs } from '../../core/commands/timing';
import type { BpmPoint, MeterMark, PackageSettings, Timing } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { MeterMap } from '../../core/time/meter';
import type { Beat, TempoMap } from '../../core/time/types';

// ---------------------------------------------------------------------------------------------------------------
// BPM values
// ---------------------------------------------------------------------------------------------------------------

/** Largest BPM the field accepts (beyond it a beat lasts less than a frame). */
export const MAX_FIELD_BPM = 10000;

/** A usable BPM: finite, > 0 and not absurd. */
export function isValidBpm(v: number): boolean {
  return Number.isFinite(v) && v > 0 && v <= MAX_FIELD_BPM;
}

/** Parses typed BPM text ("174", "128.5", "128,5"); null when it is not a usable BPM. */
export function parseBpm(text: string): number | null {
  const s = text.trim().replace(',', '.');
  if (!/^[+]?(\d+(\.\d*)?|\.\d+)$/.test(s)) return null;
  const v = Number(s);
  return isValidBpm(v) ? v : null;
}

/** BPM shown in the field (2 decimals). */
export function formatBpm(v: number): string {
  return Number.isFinite(v) ? v.toFixed(2) : '';
}

/** Rounds a BPM to 0.0001 (keeps typed decimals, removes float noise of steps). */
export function cleanBpm(v: number): number {
  return Math.round(v * 10000) / 10000;
}

/** Arrow / button step of the BPM field: 0.1, Ctrl or Alt 0.01, Shift 1. */
export function bpmStep(mod: {
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
  readonly shiftKey?: boolean;
}): number {
  if (mod.shiftKey === true) return 1;
  if (mod.ctrlKey === true || mod.metaKey === true || mod.altKey === true) return 0.01;
  return 0.1;
}

/** `bpm + dir * step` (float noise removed), or null when the result is not a usable BPM. */
export function steppedBpm(bpm: number, dir: 1 | -1, step: number): number | null {
  const next = cleanBpm(bpm + dir * step);
  return isValidBpm(next) ? next : null;
}

export type StrengthLevel = 'strong' | 'medium' | 'weak';

/** Strength label of a tempo candidate (0..1, the beat-grid F-measure of core/audio-dsp/tempo.ts). */
export function strengthLevel(strength: number): StrengthLevel {
  if (strength >= 0.75) return 'strong';
  if (strength >= 0.45) return 'medium';
  return 'weak';
}

// ---------------------------------------------------------------------------------------------------------------
// Offsets
// ---------------------------------------------------------------------------------------------------------------

/** The offset Phira hears (ms): META.offset + info.yml offset. */
export function heardOffsetMs(timing: Pick<Timing, 'offsetMs'>, pkg: Pick<PackageSettings, 'infoOffsetSec'>): number {
  return timing.offsetMs + pkg.infoOffsetSec * 1000;
}

/**
 * The `Core.tempo.setOffset` argument that makes Phira hear beat 0 at `heardMs`: setOffset keeps the per-chart
 * adjustment (whole ms of info.yml offset) separate and splits the rest.
 */
export function setOffsetArgFor(heardMs: number, pkg: Pick<PackageSettings, 'infoOffsetSec'>): number {
  return roundMicro(heardMs - infoAdjustmentMs(pkg.infoOffsetSec));
}

/** The imported per-chart adjustment in ms (0 when none). */
export function perChartAdjustmentMs(pkg: Pick<PackageSettings, 'infoOffsetSec'>): number {
  return infoAdjustmentMs(pkg.infoOffsetSec);
}

/** Music time (s, as the browser decodes the file) at which beat 0 plays: heard offset + preview audio delay. */
export function decodedShiftSec(heardMs: number, previewAudioDelayMs: number): number {
  return (heardMs + previewAudioDelayMs) / 1000;
}

function roundMicro(ms: number): number {
  return Math.round(ms * 1000) / 1000;
}

// ---------------------------------------------------------------------------------------------------------------
// By ear
// ---------------------------------------------------------------------------------------------------------------

/** Steps of "Too early / Too late" (ms): each answer uses the next, the last repeats. */
export const BY_EAR_STEPS: readonly number[] = [10, 5, 2, 1];

export type ByEarAnswer = 'tooEarly' | 'justRight' | 'tooLate';

/** Step (ms) of the answer number `index` (0-based). */
export function byEarStepMs(index: number): number {
  return BY_EAR_STEPS[Math.min(BY_EAR_STEPS.length - 1, Math.max(0, Math.floor(index)))] ?? 1;
}

/**
 * Offset change (ms) of an answer and the next step index. "Too early" = the click comes before the music's beat,
 * so beat 0 moves later (+); "Too late" moves it earlier (-); "Just right" changes nothing and restarts the steps.
 */
export function byEarDelta(answer: ByEarAnswer, index: number): { readonly deltaMs: number; readonly next: number } {
  if (answer === 'justRight') return { deltaMs: 0, next: 0 };
  const step = byEarStepMs(index);
  return { deltaMs: answer === 'tooEarly' ? step : -step, next: index + 1 };
}

// ---------------------------------------------------------------------------------------------------------------
// Tap tempo
// ---------------------------------------------------------------------------------------------------------------

/** Taps needed before the tempo is shown. */
export const TAP_MIN = 4;
/** Idle time (ms) after which a tap starts a new series. */
export const TAP_RESET_MS = 2000;
/** Taps kept for the running average. */
export const TAP_MAX = 64;

export interface TapState {
  /** Tap times (ms, performance clock) of the current series. */
  readonly taps: readonly number[];
}

export interface TapReading {
  readonly count: number;
  /** Running average BPM (null before TAP_MIN taps). */
  readonly bpm: number | null;
  /** Standard deviation of the tempo implied by each interval (BPM), null before TAP_MIN taps. */
  readonly spreadBpm: number | null;
}

export const EMPTY_TAPS: TapState = { taps: [] };

/** Adds a tap at `nowMs` (a new series when the last tap is older than TAP_RESET_MS or the clock went back). */
export function addTap(state: TapState, nowMs: number): TapState {
  const last = state.taps[state.taps.length - 1];
  if (last === undefined || nowMs - last > TAP_RESET_MS || nowMs <= last) return { taps: [nowMs] };
  const taps = [...state.taps, nowMs];
  return { taps: taps.length > TAP_MAX ? taps.slice(taps.length - TAP_MAX) : taps };
}

/** Whether the series is still running at `nowMs` (else the next tap starts over). */
export function tapsActive(state: TapState, nowMs: number): boolean {
  const last = state.taps[state.taps.length - 1];
  return last !== undefined && nowMs - last <= TAP_RESET_MS;
}

export function readTaps(state: TapState): TapReading {
  const n = state.taps.length;
  if (n < TAP_MIN) return { count: n, bpm: null, spreadBpm: null };
  const first = state.taps[0] ?? 0;
  const last = state.taps[n - 1] ?? 0;
  const mean = (last - first) / (n - 1);
  if (!(mean > 0)) return { count: n, bpm: null, spreadBpm: null };
  const bpm = 60000 / mean;
  let sum = 0;
  for (let i = 1; i < n; i++) {
    const b = 60000 / ((state.taps[i] ?? 0) - (state.taps[i - 1] ?? 0));
    sum += (b - bpm) ** 2;
  }
  return { count: n, bpm, spreadBpm: Math.sqrt(sum / (n - 1)) };
}

// ---------------------------------------------------------------------------------------------------------------
// Tempo table rows
// ---------------------------------------------------------------------------------------------------------------

export interface TempoRow {
  readonly t: Beat;
  /** The BPM point at this beat, if any. */
  readonly point: BpmPoint | null;
  /** The meter mark at this beat, if any. */
  readonly mark: MeterMark | null;
  /** Effective tempo and meter at this beat (inherited when the row does not set them). */
  readonly bpm: number;
  readonly beatsPerBar: number;
  /** The beat-0 row: its beat is fixed and it cannot be deleted. */
  readonly pinned: boolean;
}

/** Rows of the tempo-changes table (BPM points and meter marks merged by beat, beat order). */
export function tempoRows(points: readonly BpmPoint[], meter: readonly MeterMark[]): TempoRow[] {
  const byKey = new Map<string, { t: Beat; point: BpmPoint | null; mark: MeterMark | null }>();
  for (const p of points) byKey.set(Beats.key(p.t), { t: p.t, point: p, mark: null });
  for (const m of meter) {
    const k = Beats.key(m.t);
    const hit = byKey.get(k);
    if (hit !== undefined) hit.mark = m;
    else byKey.set(k, { t: m.t, point: null, mark: m });
  }
  const sorted = [...byKey.values()].sort((a, b) => Beats.cmp(a.t, b.t));
  const out: TempoRow[] = [];
  let bpm = points.find((p) => Beats.eq(p.t, Beats.ZERO))?.bpm ?? points[0]?.bpm ?? 120;
  let perBar = 4;
  for (const r of sorted) {
    if (r.point !== null) bpm = r.point.bpm;
    if (r.mark !== null) perBar = r.mark.beatsPerBar;
    out.push({ t: r.t, point: r.point, mark: r.mark, bpm, beatsPerBar: perBar, pinned: Beats.eq(r.t, Beats.ZERO) });
  }
  return out;
}

/** The meter with the mark at `t` set to `beatsPerBar` (added when missing), sorted. */
export function meterWith(meter: readonly MeterMark[], t: Beat, beatsPerBar: number): MeterMark[] {
  const out = meter.filter((m) => !Beats.eq(m.t, t)).map((m) => ({ t: m.t, beatsPerBar: m.beatsPerBar }));
  out.push({ t, beatsPerBar });
  return out.sort((a, b) => Beats.cmp(a.t, b.t));
}

/** The meter without the mark at `t` (the mark at beat 0 always stays). */
export function meterWithout(meter: readonly MeterMark[], t: Beat): MeterMark[] {
  if (Beats.eq(t, Beats.ZERO)) return [...meter];
  return meter.filter((m) => !Beats.eq(m.t, t)).map((m) => ({ t: m.t, beatsPerBar: m.beatsPerBar }));
}

/** Parses a beats-per-bar entry (integer or decimal like 3.5 for 7/8), null when unusable. */
export function parseBeatsPerBar(text: string): number | null {
  const v = Number(text.trim().replace(',', '.'));
  return Number.isFinite(v) && v > 0 && v <= 64 ? v : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Bars in music time
// ---------------------------------------------------------------------------------------------------------------

export interface BarSpan {
  /** Bar number (1 = the bar containing beat 0). */
  readonly bar: number;
  readonly startBeat: Beat;
  /** Length of the bar in beats (a bar cut short by a meter change is shorter). */
  readonly lenBeats: number;
  /** Music time (s, decoded) of the bar start and the bar length (s). */
  readonly startSec: number;
  readonly lenSec: number;
  readonly beatsPerBar: number;
}

/** `count` bars from bar `firstBar` on (bar spans in music seconds; `shiftSec` = decoded time of beat 0). */
export function barSpans(
  tempo: TempoMap,
  meter: MeterMap,
  shiftSec: number,
  firstBar: number,
  count: number,
): BarSpan[] {
  const out: BarSpan[] = [];
  if (!(count > 0)) return out;
  let start: Beat;
  try {
    start = meter.barStart(Math.trunc(firstBar));
  } catch {
    return out;
  }
  for (let i = 0; i < count; i++) {
    let next: Beat;
    try {
      next = meter.nextBarStart(start);
    } catch {
      break;
    }
    const b0 = Beats.toF64(start);
    const b1 = Beats.toF64(next);
    const s0 = tempo.secAtBeatF64(b0);
    const s1 = tempo.secAtBeatF64(b1);
    out.push({
      bar: meter.barAt(start),
      startBeat: start,
      lenBeats: b1 - b0,
      startSec: s0 + shiftSec,
      lenSec: s1 - s0,
      beatsPerBar: meter.beatsPerBarAt(start),
    });
    start = next;
  }
  return out;
}

/** Bar number playing at decoded music time `sec`. */
export function barAtMusicSec(tempo: TempoMap, meter: MeterMap, shiftSec: number, sec: number): number {
  const beat = beatF64AtChartSec(tempo, sec - shiftSec);
  return meter.barAt(Beats.fromF64(beat, 1920));
}

/** Beat (f64) at chart seconds, extending the first tempo before 0 (Phira clamps the chart, the music does not). */
export function beatF64AtChartSec(tempo: TempoMap, sec: number): number {
  if (sec >= 0) return tempo.beatF64At(sec);
  const bpm = tempo.points[0]?.bpm ?? 120;
  return sec / (60 / bpm);
}

/** Chart seconds of a beat (f64), extending the first tempo before beat 0. */
export function chartSecAtBeatF64(tempo: TempoMap, beat: number): number {
  if (beat >= 0) return tempo.secAtBeatF64(beat);
  const bpm = tempo.points[0]?.bpm ?? 120;
  return beat * (60 / bpm);
}

/** The BPM list in the worker protocol's shape (`{ beat, bpm }` with f64 beats), sorted. */
export function protocolBpmList(points: readonly BpmPoint[]): Array<{ readonly beat: number; readonly bpm: number }> {
  return [...points].sort((a, b) => Beats.cmp(a.t, b.t)).map((p) => ({ beat: Beats.toF64(p.t), bpm: p.bpm }));
}

/** The BPM point in effect at a beat (the last one at or before it). */
export function pointAtBeat(points: readonly BpmPoint[], beat: Beat): BpmPoint | null {
  let best: BpmPoint | null = null;
  for (const p of points) {
    if (Beats.cmp(p.t, beat) <= 0 && (best === null || Beats.cmp(p.t, best.t) > 0)) best = p;
  }
  return best;
}

/**
 * The strongest onset of the envelope within `windowSec` of `sec` (parabolic peak time), or `sec` itself when
 * there is none: "click a downbeat" lands on the attack the user aimed at.
 */
export function snapToOnset(e: OnsetEnvelope | null, sec: number, windowSec = 0.04): number {
  if (e === null || !Number.isFinite(sec)) return sec;
  const env = e.env;
  const i0 = Math.max(1, Math.floor((sec - windowSec) / e.hopSec));
  const i1 = Math.min(env.length - 2, Math.ceil((sec + windowSec) / e.hopSec));
  let best = -1;
  let at = -1;
  for (let i = i0; i <= i1; i++) {
    const v = env[i] ?? 0;
    if (v > best && v >= (env[i - 1] ?? 0) && v >= (env[i + 1] ?? 0)) {
      best = v;
      at = i;
    }
  }
  if (at < 0 || best < MIN_SNAP_ONSET) return sec;
  const a = env[at - 1] ?? 0;
  const c = env[at + 1] ?? 0;
  const denom = a - 2 * best + c;
  const delta = denom < 0 ? Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / denom)) : 0;
  return (at + delta) * e.hopSec;
}

/** Weakest envelope peak a click snaps to. */
const MIN_SNAP_ONSET = 0.05;
